import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";

const UserProfilePage = () => {
  const { user, company } = useAppContext();
  const navigate = useNavigate();

  const companyData = company?.client || company || {};
  const logoSrc = companyData.logo
    ? companyData.logo.startsWith("data:")
      ? companyData.logo
      : `data:image/png;base64,${companyData.logo}`
    : "/default-logo.png";

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
        <h2 className="text-xl font-normal mb-2">No user logged in</h2>
        <p className="text-gray-600 text-sm max-w-xs text-center">
          Please login to view your profile.
        </p>
      </div>
    );
  }

  return (
    <div
      className="
        w-80         /* half previous width (3xl → xl) */
        ml-auto           /* align right side */
        mt-0
        p-4               /* smaller padding */
        bg-white 
        rounded-md
        shadow-md 
        font-sans 
        text-gray-800
        text-sm            /* smaller font size for all text */
      "
      style={{ minHeight: "80vh" }}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-6 text-blue-600 hover:text-blue-800 rounded px-3 py-1 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition duration-150 font-medium focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        &larr; 
      </button>

      {/* Main Header: User info */}
      <div className="flex items-center gap-4 mb-8">
        <div className="flex-shrink-0">
          <img
            src={logoSrc}
            alt={companyData.name || "Company Logo"}
            className="h-12 w-12 rounded-full object-cover border border-blue-300 shadow-sm"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/default-logo.png";
            }}
          />
        </div>
        <div>
          <h1 className="text-lg font-normal mb-1">{user.name}</h1>
          <div className="text-gray-700 text-xs">
            <span>{user.email}</span>
            {user.phone && (
              <>
                <span className="mx-1 text-gray-400">•</span>
                <span>{user.phone}</span>
              </>
            )}
          </div>
          {user.roles && (
            <div className="mt-1 text-xs text-gray-600">
              Roles: <span className="font-medium">{user.roles.join(", ")}</span>
            </div>
          )}
        </div>
      </div>

      {/* Body: Two cards for details */}
      <div className="grid grid-cols-1 gap-6">
        {/* Personal Details */}
        <section>
          <h2 className="text-sm font-normal text-blue-500 mb-3 border-b border-gray-300 pb-1 flex items-center justify-between">
            <span>User Information</span>
          </h2>
          <div className="space-y-3 text-gray-700 text-xs">
            <div>
              <span className="font-semibold">User Name:</span>
              <div>{user.name}</div>
            </div>
            <div>
              <span className="font-semibold">Email:</span>
              <div>{user.email}</div>
            </div>
            {user.phone && (
              <div>
                <span className="font-semibold">Phone:</span>
                <div>{user.phone}</div>
              </div>
            )}
            {user.roles && (
              <div>
                <span className="font-semibold">Roles:</span>
                <div>{user.roles.join(", ")}</div>
              </div>
            )}
          </div>
        </section>

        {/* Company Details Card */}
        <section>
          <h2 className="text-sm font-normal text-blue-500 mb-3 border-b border-gray-300 pb-1 flex items-center justify-between">
            <span>Company Information</span>
            {/* {companyData.logo && (
              <img
                src={logoSrc}
                alt={companyData.name || "Company Logo"}
                className="h-8 w-8 rounded-full object-cover border border-blue-300"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/default-logo.png";
                }}
              />
            )} */}
          </h2>
          {companyData.name ? (
            <div className="space-y-3 text-gray-700 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-semibold min-w-[110px]">Company Name:</span>
                <span>{companyData.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold min-w-[110px]">Email:</span>
                <span>{companyData.email || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold min-w-[110px]">Organisation ID:</span>
                <span className="font-mono tracking-wide">{companyData.client_code || "N/A"}</span>
              </div>
              {companyData.address && (
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[110px]">Address:</span>
                  <span>{companyData.address}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-xs">No company information available.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default UserProfilePage;
